/**
 * Tiny CSV export helper.
 *
 * - `toCsv(rows, columns)` builds an RFC-4180-ish CSV string from an array
 *   of objects. Each column is `{ header, get(row) }`.
 * - `downloadCsv(filename, csvString)` triggers a browser download.
 *
 * Kept small on purpose — no streaming, no external deps.
 */

const escapeCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

export const toCsv = (rows, columns) => {
  const header = columns.map((c) => escapeCell(c.header)).join(",");
  const body = rows
    .map((row) => columns.map((c) => escapeCell(c.get(row))).join(","))
    .join("\n");
  // Prepend UTF-8 BOM so Excel opens accented characters correctly.
  return `\ufeff${header}\n${body}`;
};

export const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

/**
 * Present/absent attendance status → user-facing label (Logged In / Logged Off / Absent).
 * Display-only mapping; "Half Day Leave" is eliminated.
 */
export const attendanceLabel = (record) => {
  if (!record) return "";
  if (record.status === "absent") return "Absent";
  if (record.status === "present" || record.status === "half_day_leave") {
    return record.out_ts ? "Logged Off" : "Logged In";
  }
  return record.status || "";
};

/**
 * Return late_minutes for a late record. Now read directly from the
 * server-computed late_minutes property rather than recomputing in the browser.
 */
export const lateByMinutes = (record, session) => {
  if (!record) return null;
  if (record.late_minutes !== undefined && record.late_minutes !== null) {
    return record.late_minutes;
  }
  // Fallback if record has is_late but late_minutes wasn't set
  if (record.is_late && session && record.scan_in_ts) {
    const shiftStartMs = new Date(`${session.date}T${session.shift_start}:00`).getTime();
    const scanMs = new Date(record.scan_in_ts).getTime();
    const diffMin = Math.round((scanMs - shiftStartMs) / 60_000);
    return diffMin > 0 ? diffMin : null;
  }
  return null;
};
